import { AnimatePresence, motion } from 'framer-motion'
import React, { use, cloneElement, createContext, ReactElement } from 'react'
import { objectHookToContext } from '~/common/hookToContext'
import { Msg, useMsg } from '~/common/localizationUtils'
import { TOP_NAV_HEIGHT_PX } from '~/common/scrollUtils'
import {
  asLink,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  forwardRef,
  Spacer,
  useDisclosure,
  UseDisclosureReturn
} from '~/design_system'
import { ChevronLeft, ChevronRight } from '~/icons'
import { useMenuItemProps, MenuItems, DynamicSingleButtonOrMenuList } from '~/navigation_menu/constants'
import { ignoreProviderMissingError } from '~/common/reactUtils'

const DrawerContext = createContext<{ disclosure: UseDisclosureReturn | null }>({ disclosure: null })
export function MainMenusMobile({ disclosure }: { disclosure: UseDisclosureReturn }) {
  const menuItems = useMenuItemProps({ isMobile: true })
  const msg = useMsg()

  return (
    <DrawerContext value={{ disclosure }}>
      <Box sx={{ '@media(min-width: 1350px)': { display: 'none' }, '@media print': { display: 'none' } }}>
        <Drawer size="xs" placement="left" isOpen={disclosure.isOpen} onClose={disclosure.onClose}>
          <DrawerOverlay top={TOP_NAV_HEIGHT_PX} />
          <DrawerContent
            top={`${TOP_NAV_HEIGHT_PX} !important`}
            boxShadow={getBoxShadowThatLooksLikeUnderTopNav()}
            borderTop="1px"
            borderColor="system.color.border.container">
            <DrawerBody data-testid="mobile-menu-drawer" as="nav" aria-label={msg('nav.main_menu')} p={0}>
              <MobileMenuButton {...menuItems.home.top} />
              <MobileDynamicMenuList mobileMenuId="mobile_sow_menu" menuItems={menuItems.sow_menu} />
              <MobileDynamicMenuList mobileMenuId="mobile_jobs_menu" menuItems={menuItems.jobs_menu} />
              <MobileDynamicMenuList mobileMenuId="mobile_people_menu" menuItems={menuItems.people_menu} />
              <MobileMenuButton {...menuItems.work_orders.top} />
              <MobileDynamicMenuList mobileMenuId="mobile_invoices_menu" menuItems={menuItems.invoices_menu} />
              <MobileDynamicMenuList mobileMenuId="mobile_timesheets_menu" menuItems={menuItems.timesheets_menu} />
              <MobileMenuButton {...menuItems.dashboards.top} />
              <MobileDynamicMenuList mobileMenuId="mobile_reports_menu" menuItems={menuItems.reports_menu} />
              {!menuItems.more_menu.top.isHidden && (
                <MobileMenu id="mobile_more_menu">
                  <MobileMenuButton {...menuItems.more_menu.top} />
                  <MobileMenuList>
                    <MobileMenuItemLink {...menuItems.more_menu.approvals} />
                    <MobileMenuItemLink {...menuItems.more_menu.documents} />
                    <MobileMenuItemLink {...menuItems.more_menu.vendors} />
                    <MobileMenuItemLink {...menuItems.more_menu.users} />
                    <MobileMenuItemLink {...menuItems.more_menu.program_teams} />
                    <MobileMenuItemLink {...menuItems.more_menu.company_profile} />
                    <MobileMenuItemLink {...menuItems.more_menu.worker_tracking} />
                    <MobileMenuItemLink {...menuItems.more_menu.expenses} />
                    <MobileMenuItemLink {...menuItems.more_menu.bulk_updates} />
                    <MobileMenuItemLink {...menuItems.more_menu.checklists} />
                    <MobileMenuItemLink {...menuItems.more_menu.position_management} />
                    <MobileMenuItemLink {...menuItems.more_menu.api_docs} />
                    <MobileMenuItemLink {...menuItems.more_menu.tenant_metadata} />
                    <MobileMenuItemLink {...menuItems.more_menu.company_settings} />
                  </MobileMenuList>
                </MobileMenu>
              )}
              <MobileMenuButton {...menuItems.candidate_timesheets.top} />
              <MobileMenuButton {...menuItems.candidate_expenses.top} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>
    </DrawerContext>
  )
}

// MENU COMPONENTS

const [MobileMenuProvider, useMobileMenu] = objectHookToContext(({ id }: { id?: string }) => {
  const disclosure = useDisclosure({ id })
  return { id, ...disclosure }
})

const MobileMenu = (props: BoxProps) => {
  return (
    <MobileMenuProvider id={props.id}>
      <Box {...props} />
    </MobileMenuProvider>
  )
}

function MobileDynamicMenuList({ mobileMenuId, menuItems }: { mobileMenuId: string; menuItems: MenuItems }) {
  return (
    <DynamicSingleButtonOrMenuList
      menuItems={menuItems}
      renderSingle={item => <MobileMenuButton {...item} />}
      renderMultiple={(topItem, subItems) => (
        <MobileMenu id={mobileMenuId}>
          <MobileMenuButton {...topItem} />
          <MobileMenuList>
            {Object.entries(subItems).map(([key, item]) => {
              return <MobileMenuItemLink key={key} {...item} />
            })}
          </MobileMenuList>
        </MobileMenu>
      )}
    />
  )
}

type MobileMenuButtonProps = Omit<ButtonProps, 'leftIcon'> & {
  href?: string
  isHidden?: boolean
  leftIcon?: ReactElement
}

const MobileMenuButton = forwardRef<MobileMenuButtonProps, 'button'>(
  ({ href, leftIcon, rightIcon, children, isHidden, ...props }, ref) => {
    const [mobileMenu] = ignoreProviderMissingError(useMobileMenu)
    const opensMenuList = mobileMenu && !href && !props.onClick
    const drawerContext = use(DrawerContext)

    if (isHidden) return null

    return (
      <Button
        size="none"
        variant="mobileMenu"
        ref={ref}
        {...asLink(href)}
        role={href ? 'menuitem' : undefined}
        onClick={opensMenuList ? mobileMenu?.onToggle : drawerContext.disclosure?.onClose}
        {...(opensMenuList && {
          'aria-expanded': mobileMenu?.isOpen,
          'aria-controls': `menu-list-${mobileMenu?.id}`
        })}
        {...props}>
        {leftIcon && cloneElement(leftIcon as ReactElement<{ className?: string }>, { className: 'menu-button__icon' })}
        {children}
        <Spacer />
        {opensMenuList && <ChevronRight className="mobile-button__chevron" />}
      </Button>
    )
  }
)

MobileMenuButton.displayName = 'MobileMenuButton'

const MobileMenuList = ({ children, ...props }: BoxProps) => {
  const mobileMenu = useMobileMenu()
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        width: 'full',
        height: 'full',
        zIndex: 'modal',
        pointerEvents: mobileMenu?.isOpen ? undefined : 'none'
      }}>
      <AnimatePresence>
        {mobileMenu?.isOpen && (
          <motion.div
            key="mobile-menu-sub-menu"
            animate={{ opacity: 1, translateX: 0 }}
            initial={{ opacity: 0, translateX: 25 }}
            exit={{ opacity: 0, translateX: 25 }}
            style={{ height: '100%', width: '100%' }}
            transition={{ ease: 'easeIn', duration: 0.15 }}>
            <Box
              id={mobileMenu?.id}
              role="menu"
              sx={{
                background: 'white',
                width: 'full',
                height: 'full',
                overflowY: 'auto',
                boxShadow: getBoxShadowThatLooksLikeUnderTopNav()
              }}
              {...props}>
              <MobileMenuButton leftIcon={<ChevronLeft />} onClick={mobileMenu?.onClose}>
                <Msg id="common.ui.back" />
              </MobileMenuButton>
              {children}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export const MobileMenuItemLink = MobileMenuButton

const getBoxShadowThatLooksLikeUnderTopNav = () => 'inset 0 5px 7px -7px rgba(0,0,0,0.4)'
